import {
  Grid,
  Table,
  Card,
  Caption,
  Autocomplete,
  IOption,
  Skeleton,
  AutocompleteProps,
} from '@design-systems/canvas-ui'
import { useState, useContext, useMemo, useCallback, useEffect } from 'react'
import { usersSearchColumnDefs } from '../utils/userTableHelpers'
import { useLazyQuery, useQuery } from '@apollo/client/react'
import {
  GET_ACCREDITATION_TYPES,
  SEARCH_USERS_AUTOCOMPLETE,
  USERS_SEARCH_QUERY,
} from 'graphql/queries/users/queries/userQueries'
import {
  useAllCurrentCoreFilters,
  useConjunctiveQueryString,
  CoreFilterAutocompleteSearchDropdown,
  CoreFilterMultiselect,
  CurrentCoreFilterChips,
  CoreClearAllFiltersButton,
  CoreFilterDropdown,
} from '@plm-core/sort-and-filter'
import { debounce } from 'lodash'
import {
  ContextualizedPagination,
  useContextualizedUpdateTotals,
  usePageLoadErrorToast,
  useContextualizedPaging,
} from '@plm-core/utils'
import { UserContext } from 'App/UserContext'
import { getUserDisplayName } from 'users/utils/userDisplayHelpers'
import useBusinessPartnersAutocompleteSetup from 'hooks/useBusinessPartnersAutocompleteSetup'
import UserViewDrawer from 'users/components/UserViewDrawer'

// Canvas-ui doesn't export the RenderOption arg type, so derive it from
// the `renderOption` prop on AutocompleteProps.
type RenderOption = Parameters<
  NonNullable<AutocompleteProps['renderOption']>
>[0]

type UserData = {
  id: string
  firstName?: string
  lastName?: string
  email?: string
  companyName?: string
}

// Canvas requires id and value to match, so free-text options use a reserved
// identity while keeping the original query in searchText.
type UserOption =
  | (IOption & {
      type: 'network'
      data: UserData
    })
  | (IOption & {
      type: 'free-text'
      searchText: string
    })

const DEBOUNCE_MS = 300
const FREE_TEXT_OPTION_PREFIX = 'free-text:'

const mapQueryToAutocompleteOption = (query: string): UserOption => {
  const optionIdentity = `${FREE_TEXT_OPTION_PREFIX}${query}`

  return {
    value: optionIdentity,
    label: `Search for "${query}"`,
    id: optionIdentity,
    type: 'free-text',
    searchText: query,
  }
}

const getQueryStringMatcher = (searchQuery: string) => {
  return `(firstName:${searchQuery}* OR lastName:${searchQuery}* OR email:${searchQuery}*)`
}

const getQueryStringForInternal = (searchQuery: string) => {
  return getQueryStringMatcher(searchQuery)
}

const getQueryStringForExternal = (
  searchQuery: string,
  partnerId: number | null,
) => {
  const partnerIdMatcher = partnerId
    ? `(internal:true OR partnerId:${partnerId})`
    : '(internal:true)'
  return `${partnerIdMatcher} AND ${getQueryStringMatcher(searchQuery)}`
}

const statusFilterSetup = {
  label: 'Status',
  id: 'active',
  fieldId: 'active',
}

const accreditationFilterSetup = {
  label: 'Accreditation',
  id: 'accreditation',
  fieldId: 'accreditation',
}

const UsersSearchBody = () => {
  const { isInternal, sparkUser } = useContext(UserContext)
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null)
  // Safely parse the partnerId from sparkUser, handling both string and number types
  const currentPartnerId = useMemo(() => {
    const parsedPartnerId = Number(sparkUser?.partnerId)
    return Number.isFinite(parsedPartnerId) ? parsedPartnerId : null
  }, [sparkUser])

  // Search and filter
  const [searchQuery, setSearchQuery] = useState<UserOption | null>(null)
  const currentFilters = useAllCurrentCoreFilters()
  const partnerIds = useMemo(
    () =>
      currentFilters
        .filter((filter) => filter.fieldId === 'partnerId')
        .map((filter) => Number(filter.value)) || [],
    [currentFilters],
  )
  const { queryString } = useConjunctiveQueryString(['active', 'accreditation'])
  const { handleBusinessPartnerSearchChange } =
    useBusinessPartnersAutocompleteSetup()

  const [fetchUsers] = useLazyQuery(SEARCH_USERS_AUTOCOMPLETE, {
    fetchPolicy: 'no-cache',
  })

  const {
    data: accreditationTypesData,
    loading: accreditationTypesLoading,
    error: accreditationTypesError,
  } = useQuery(GET_ACCREDITATION_TYPES, {
    variables: { userType: 'EXTERNAL' },
  })

  const accreditationTypeOptions = useMemo(() => {
    const results = accreditationTypesData?.getAccreditationTypes || []
    return results.map((type) => ({
      value: type.code,
      label: `${type.code} - ${type.description}`,
    }))
  }, [accreditationTypesData])

  const debouncedUsersFetch = useMemo(
    () =>
      debounce(
        async (query: string, resolve: (options: UserOption[]) => void) => {
          const queryString = isInternal
            ? getQueryStringForInternal(query)
            : getQueryStringForExternal(query, currentPartnerId)

          try {
            const { data } = await fetchUsers({
              variables: { q: queryString },
            })
            const results = data?.searchUsers?.content || []
            const options: UserOption[] = results.map((item) => ({
              value: item.id,
              label: getUserDisplayName(item.firstName, item.lastName),
              id: item.id,
              type: 'network',
              data: item,
            }))
            options.unshift(mapQueryToAutocompleteOption(query))
            resolve(options)
          } catch (error) {
            console.error('Error fetching users suggestions:', error)
            resolve([mapQueryToAutocompleteOption(query)])
          }
        },
        DEBOUNCE_MS,
      ),
    [currentPartnerId, fetchUsers, isInternal],
  )

  const handleSearchChange = useCallback(
    (searchText: string) =>
      new Promise<UserOption[]>((resolve) => {
        const sanitized = searchText?.trim() || ''
        if (sanitized.length < 3) {
          resolve([mapQueryToAutocompleteOption(sanitized)])
          return
        }
        debouncedUsersFetch(sanitized, resolve)
      }),
    [debouncedUsersFetch],
  )

  // Final query string for the main users search query and query setup
  const finalQueryString = useMemo(() => {
    let query = isInternal
      ? getQueryStringForInternal('')
      : getQueryStringForExternal('', currentPartnerId)

    if (searchQuery) {
      if (searchQuery.type === 'free-text') {
        query += ` AND (firstName:${searchQuery.searchText}* OR lastName:${searchQuery.searchText}* OR email:${searchQuery.searchText}*)`
      } else {
        query += ` AND (userProfileId:${searchQuery.id})`
      }
    }
    if (queryString) {
      query += ` AND ${queryString}`
    }
    return query
  }, [isInternal, currentPartnerId, searchQuery, queryString])

  const { currentPage, pageSize, goToFirstPage } = useContextualizedPaging()

  const [currentQueryStringForSearch, setCurrentQueryStringForSearch] =
    useState(finalQueryString)
  const [currentPartnerIds, setCurrentPartnerIds] = useState(partnerIds)

  useEffect(() => {
    if (finalQueryString !== currentQueryStringForSearch) {
      goToFirstPage()
      setCurrentQueryStringForSearch(finalQueryString)
    }
    if (partnerIds !== currentPartnerIds) {
      goToFirstPage()
      setCurrentPartnerIds(partnerIds)
    }
  }, [
    finalQueryString,
    currentQueryStringForSearch,
    partnerIds,
    currentPartnerIds,
    goToFirstPage,
  ])
  const shouldSkipQuery = useMemo(() => {
    return !searchQuery && !queryString && currentPartnerIds.length === 0
  }, [searchQuery, queryString, currentPartnerIds])
  const { data, loading, error, refetch } = useQuery(USERS_SEARCH_QUERY, {
    variables: {
      partnerIds: currentPartnerIds,
      page: currentPage,
      q: currentQueryStringForSearch,
      size: pageSize,
    },
    skip: shouldSkipQuery,
  })
  const users = data?.searchUsers?.content || []

  usePageLoadErrorToast({
    error,
    toastConfig: {
      variant: 'error',
      heading: 'Unable to load users',
      message:
        'We are unable to load the users at this time. Please try again or contact support.',
    },
  })

  useContextualizedUpdateTotals({
    totalElements: data?.searchUsers?.pageInfo?.totalElements,
    totalPages: data?.searchUsers?.pageInfo?.totalPages,
  })

  // TODO: Implement sorting state management and pass it to the Table component. This is a placeholder for now.
  const [sortingState, setSortingState] = useState()

  const handleUserNameClick = useCallback((item: any) => {
    setSelectedUser(item)
  }, [])

  const handleUserViewDrawerClose = useCallback(() => {
    setSelectedUser(null)
  }, [])

  const renderOption = useCallback(({ option }: RenderOption) => {
    const userOption = option as UserOption
    if (userOption.type === 'free-text') {
      return userOption.label
    }
    return (
      <Caption
        below={`${userOption.data?.companyName || 'Target'} - ${userOption.data?.email}`}
      >
        {userOption.label}
      </Caption>
    )
  }, [])

  return (
    <Grid.Container>
      {selectedUser && (
        <UserViewDrawer
          userProfileId={selectedUser.id}
          isOpen={!!selectedUser}
          onRequestClose={handleUserViewDrawerClose}
          refetchUser={refetch}
        />
      )}
      <Grid.Item size={{ xs: 12 }}>
        <Grid.Container align="center">
          <Grid.Item size={{ xs: 4 }}>
            <Autocomplete
              placeholder="Search name or email"
              options={handleSearchChange as any}
              renderOption={renderOption}
              value={searchQuery as any}
              onChange={(option: any) => setSearchQuery(option)}
            />
          </Grid.Item>
          <Grid.Item size={{ xs: true }}>
            <Grid.Container>
              {isInternal && (
                <Grid.Item>
                  <CoreFilterAutocompleteSearchDropdown
                    label="Company"
                    id="partnerId"
                    fieldId="partnerId"
                    options={handleBusinessPartnerSearchChange as any}
                  />
                </Grid.Item>
              )}
              {!accreditationTypesLoading && !accreditationTypesError && (
                <Grid.Item>
                  <CoreFilterDropdown {...accreditationFilterSetup}>
                    <CoreFilterMultiselect
                      {...accreditationFilterSetup}
                      isSearchHidden={true}
                      options={accreditationTypeOptions}
                    />
                  </CoreFilterDropdown>
                </Grid.Item>
              )}
              {accreditationTypesLoading && (
                <Grid.Item size={{ xs: 2 }}>
                  <Skeleton />
                </Grid.Item>
              )}
              <Grid.Item>
                <CoreFilterDropdown {...statusFilterSetup}>
                  <CoreFilterMultiselect
                    {...statusFilterSetup}
                    isSearchHidden={true}
                    options={[
                      {
                        value: 'true',
                        label: 'Active',
                      },
                      {
                        value: 'false',
                        label: 'Inactive',
                      },
                    ]}
                  />
                </CoreFilterDropdown>
              </Grid.Item>
            </Grid.Container>
          </Grid.Item>
        </Grid.Container>
      </Grid.Item>
      {currentFilters.length > 0 && (
        <Grid.Item size={{ xs: 12 }}>
          <Grid.Container align="center">
            <Grid.Item>
              <CurrentCoreFilterChips />
            </Grid.Item>
            <Grid.Item>
              <CoreClearAllFiltersButton variant="ghost" />
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
      )}
      {shouldSkipQuery && (
        <Grid.Item size={{ xs: 12 }}>
          <Card variant="elevated" className="hc8-pa-4x">
            <Grid.Container
              align="center"
              justify="center"
              style={{ height: '400px' }}
            >
              <Grid.Item>
                <Caption below="Use the search and filters above to find users.">
                  Start your search
                </Caption>
              </Grid.Item>
            </Grid.Container>
          </Card>
        </Grid.Item>
      )}
      {!shouldSkipQuery && (
        <>
          <Grid.Item size={{ xs: 12 }}>
            <ContextualizedPagination />
          </Grid.Item>
          <Grid.Item size={{ xs: 12 }}>
            <Card variant="elevated" className="hc8-pa-4x">
              {!loading && (
                <Table
                  name="Users List"
                  alternateRowColor
                  data={{
                    columnDefs: usersSearchColumnDefs({
                      onUserNameClick: handleUserNameClick,
                    }),
                    rowData: users,
                  }}
                  mode="server"
                  sorting={{
                    sortingState: sortingState as any,
                    onSortingChange: (newSortingState) =>
                      setSortingState(newSortingState as any),
                  }}
                  hasGrid={false}
                  isCornerRounded
                  highlightOnHover="row"
                />
              )}
              {loading && <Skeleton variant="rectangle" height="400px" />}
            </Card>
          </Grid.Item>
          <Grid.Item size={{ xs: 12 }}>
            <ContextualizedPagination />
          </Grid.Item>
        </>
      )}
    </Grid.Container>
  )
}

export default UsersSearchBody
